// Countries registry browser (Platform Ops Console, F6). Read view over the authoritative
// Country Configuration: currencies, legal identifiers, payment methods, and registration
// requirements per jurisdiction. See docs/country-configuration-and-currency.md.
import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Drawer, Tabs, Descriptions, Typography, Space, Empty, message, Card, Modal, Input } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, StatusTag } from '../../components/console';
import { useAppSelector } from '../../app/hooks';
import { selectRoles } from '../../features/auth/authSlice';
import CountryConfigEditor from './CountryConfigEditor';
import {
  listCountries, getCountry,
  type CountrySummary, type CountryConfigDetail, type IdentifierConfig,
} from '../../services/countryConfigService';

const { Text } = Typography;

const AUTHOR_ROLES = ['tax_officer', 'platform_admin', 'platform_owner', 'super_admin', 'system_admin'];
const PUBLISH_ROLES = ['platform_admin', 'platform_owner', 'super_admin', 'system_admin'];
type EditorMode = { kind: 'create'; country: string; cloneFromCode?: string } | { kind: 'edit'; id: string } | null;

const IdentifierCard: React.FC<{ title: string; id?: IdentifierConfig | null }> = ({ title, id }) => {
  if (!id || !id.internalCode) return null;
  return (
    <Descriptions title={title} size="small" column={1} bordered style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Label">{id.displayLabel}</Descriptions.Item>
      <Descriptions.Item label="Code"><Text code>{id.internalCode}</Text></Descriptions.Item>
      {id.issuingAuthority && <Descriptions.Item label="Authority">{id.issuingAuthority}</Descriptions.Item>}
      <Descriptions.Item label="Required">{id.required ? 'Yes' : 'No'}</Descriptions.Item>
      {id.validationPattern && <Descriptions.Item label="Pattern"><Text code>{id.validationPattern}</Text></Descriptions.Item>}
      {id.placeholder && <Descriptions.Item label="Example">{id.placeholder}</Descriptions.Item>}
      {id.uniquenessPolicy && <Descriptions.Item label="Uniqueness">{id.uniquenessPolicy}</Descriptions.Item>}
    </Descriptions>
  );
};

export function CountriesPage() {
  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState<CountryConfigDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [newCountryOpen, setNewCountryOpen] = useState(false);
  const [newCode, setNewCode] = useState('');

  const roles = useAppSelector(selectRoles);
  const canAuthor = roles.some((r) => AUTHOR_ROLES.includes(r));
  const canPublish = roles.some((r) => PUBLISH_ROLES.includes(r));

  const load = async (initial = false) => {
    initial ? setLoading(true) : setRefreshing(true);
    try {
      setCountries(await listCountries());
    } catch {
      if (initial) message.error('Could not load countries');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { void load(true); /* eslint-disable-next-line */ }, []);

  const open = async (code: string) => {
    setDetailLoading(true);
    setDetail({ country_code: code } as CountryConfigDetail);
    try { setDetail(await getCountry(code)); }
    catch { message.error('Could not load country config'); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const columns: ColumnsType<CountrySummary> = [
    { title: 'Code', dataIndex: 'country_code', width: 80, render: (c: string) => <Text strong>{c}</Text> },
    { title: 'Country', dataIndex: 'country_name' },
    { title: 'Version', dataIndex: 'version', width: 90, render: (v: number) => `v${v}` },
    { title: 'Status', width: 120, render: (_: unknown, c) => <StatusTag status={c.status === 'published' ? 'active' : c.status} label={c.status} /> },
    { title: 'Default currency', dataIndex: 'default_currency', width: 130, render: (c: string) => <Tag color="blue">{c}</Tag> },
    { title: 'Supported', render: (_: unknown, c) => (c.supported_currencies || []).map((s) => <Tag key={s}>{s}</Tag>) },
    {
      title: '', width: 220, align: 'right' as const,
      render: (_: unknown, c) => (
        <Space size={4}>
          {canAuthor && c.status === 'draft' && c.id && (
            <Button size="small" type="link" onClick={() => setEditorMode({ kind: 'edit', id: c.id! })}>Edit</Button>
          )}
          {c.status === 'in_review' && c.id && (
            <Button size="small" type="link" onClick={() => setEditorMode({ kind: 'edit', id: c.id! })}>{canPublish ? 'Review & publish' : 'Review'}</Button>
          )}
          {canAuthor && c.status === 'published' && (
            <Button size="small" type="link" onClick={() => setEditorMode({ kind: 'create', country: c.country_code, cloneFromCode: c.country_code })}>New version</Button>
          )}
          <Button size="small" type="link" onClick={() => void open(c.country_code)}>View</Button>
        </Space>
      ),
    },
  ];

  const d = detail;
  const submitNewCountry = () => {
    const code = newCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) { message.error('Enter a 2-letter ISO country code'); return; }
    setNewCountryOpen(false);
    setEditorMode({ kind: 'create', country: code });
  };
  return (
    <div style={{ padding: 24 }}>
      <Modal
        open={newCountryOpen}
        title="New country"
        okText="Continue"
        onOk={submitNewCountry}
        onCancel={() => setNewCountryOpen(false)}
        destroyOnClose
      >
        <Typography.Text className="text-sm">ISO 3166-1 alpha-2 country code (e.g. NG)</Typography.Text>
        <Input autoFocus value={newCode} maxLength={2} placeholder="NG" className="mt-1" style={{ textTransform: 'uppercase' }}
          onChange={(e) => setNewCode(e.target.value)} onPressEnter={submitNewCountry} />
      </Modal>
      <PageHeader
        title="Countries"
        subtitle="Country configuration registry — currencies, legal identifiers, payment methods, registration requirements"
        actions={
          <Space>
            {canAuthor && (
              <Button type="primary" onClick={() => { setNewCode(''); setNewCountryOpen(true); }}>New country</Button>
            )}
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>Refresh</Button>
          </Space>
        }
      />

      {loading ? <Card loading /> : countries.length === 0 ? <Empty description="No countries configured." /> : (
        <Card><Table rowKey="country_code" size="small" pagination={false} dataSource={countries} columns={columns} /></Card>
      )}

      <Drawer
        title={d ? `${d.country_name || d.country_code} · ${d.country_code}` : 'Country'}
        width={760} open={!!d} onClose={() => setDetail(null)} loading={detailLoading}
      >
        {d && d.default_currency && (
          <Tabs
            items={[
              {
                key: 'currencies', label: 'Currencies',
                children: (
                  <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="Default (functional)"><Tag color="blue">{d.default_currency}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Supported">{(d.supported_currencies || []).map((s) => <Tag key={s}>{s}</Tag>)}</Descriptions.Item>
                    <Descriptions.Item label="Locale">{d.locale || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Timezone">{d.timezone || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Payroll pack">{d.payroll_pack_country || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Effective from">{d.effective_from}</Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'identifiers', label: 'Legal Identifiers',
                children: (
                  <>
                    <IdentifierCard title="Tax identifier" id={d.tax_identifier} />
                    <IdentifierCard title="Company registration" id={d.company_identifier} />
                    <IdentifierCard title="VAT identifier" id={d.vat_identifier} />
                  </>
                ),
              },
              {
                key: 'payments', label: 'Payment Methods',
                children: (
                  <>
                    <Text strong>Methods</Text>
                    <Table style={{ marginTop: 8, marginBottom: 16 }} size="small" pagination={false} rowKey="code"
                      dataSource={d.payment_methods || []}
                      columns={[
                        { title: 'Code', dataIndex: 'code' },
                        { title: 'Channel', dataIndex: 'channel', render: (c: string) => <Tag>{c}</Tag> },
                        { title: 'Label', dataIndex: 'label' },
                      ]} />
                    <Text strong>Mobile-money providers</Text>
                    <div style={{ marginTop: 8 }}>
                      {(d.mobile_money_providers || []).map((m) => <Tag key={m.code}>{m.label}</Tag>)}
                      {(!d.mobile_money_providers || d.mobile_money_providers.length === 0) && <Text type="secondary">—</Text>}
                    </div>
                  </>
                ),
              },
              {
                key: 'registration', label: 'Registration Requirements',
                children: (
                  <>
                    <Text strong>Organization types</Text>
                    <div style={{ marginTop: 8, marginBottom: 16 }}>
                      {(d.organization_types || []).map((o) => <Tag key={o.code}>{o.label}</Tag>)}
                    </div>
                    <Text strong>Address fields</Text>
                    <div style={{ marginTop: 8, marginBottom: 16 }}>
                      <Space wrap>
                        {(d.address_schema?.fields || []).map((f) => (
                          <Tag key={f.key} color={f.required ? 'blue' : undefined}>{f.label}{f.required ? ' *' : ''}</Tag>
                        ))}
                      </Space>
                    </div>
                    <Text strong>Required documents</Text>
                    <div style={{ marginTop: 8 }}>
                      {(d.required_documents || []).map((r) => (
                        <Tag key={r.code} color={r.required ? 'blue' : undefined}>{r.label}{r.required ? ' *' : ''}</Tag>
                      ))}
                    </div>
                  </>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      <CountryConfigEditor
        mode={editorMode}
        canPublish={canPublish}
        onClose={() => setEditorMode(null)}
        onSaved={() => { setEditorMode(null); void load(false); }}
      />
    </div>
  );
}

export default CountriesPage;
